import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import MainLayout from '../../components/Layout/MainLayout';
import Table from '../../components/Table/Table';
import FilterBar from '../../components/Filters/FilterBar';
import InvoiceModal from './InvoiceModal';
import TabBar from '../../components/Tabs/TabBar';
import CandidateDetailsPanel from '../ATS/CandidateDetailsPanel';
import { useBillingCandidates, useBillingInvoices } from './hooks/useBilling';
import { searchData, filterData, formatCurrency, formatDate } from './utils/tableHelpers';
import { exportCandidatesToCSV, exportInvoicesToCSV } from './utils/exportHelpers';
import styles from './BillingPage.module.css';
import { selectUserRole } from '../../store/selectors/authSelectors';
import { BillingCandidate, BillingInvoice, BillingFilters, FilterOptions } from './types';
import { User } from '../../types';
import { Candidate } from '../ATS/types';

// Helper functions for filter options
const getUniqueMonths = (data: BillingCandidate[]) => {
  const months = new Set(data.map((item) => item.hired_month).filter(Boolean));
  return Array.from(months).sort();
};

const getUniquePocs = (data: BillingCandidate[]) => {
  const pocs = new Set(data.map((item) => item.poc_name).filter(Boolean));
  return Array.from(pocs).sort();
};

const formatMonthYear = (dateStr: string) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${year}`;
};

interface BillingPageProps {
  user: User | null;
  onLogout: () => void;
}

const BillingPage: React.FC<BillingPageProps> = ({ user, onLogout }) => {
  const userRole = useSelector(selectUserRole);
  const isPOC = userRole === 'OUTLET_POC';
  const [activeTab, setActiveTab] = useState<'candidates' | 'invoices'>('candidates');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<BillingFilters>({
    month: 'all',
    poc: 'all',
    status: 'all',
  });

  // Hooks
  const { candidates, loading: candidatesLoading, fetchCandidates } = useBillingCandidates();
  const { invoices, loading: invoicesLoading, fetchInvoices } = useBillingInvoices();

  // Modal/Panel states
  const [selectedCandidate, setSelectedCandidate] = useState<BillingCandidate | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<BillingInvoice | null>(null);

  // Load billing data on mount
  useEffect(() => {
    fetchCandidates();
    if (!isPOC) {
      fetchInvoices();
    }
  }, [fetchCandidates, fetchInvoices, isPOC]);

  // Reset filters and search when switching tabs
  useEffect(() => {
    setSearchTerm('');
    setFilters({
      month: 'all',
      poc: 'all',
      status: 'all',
    });
  }, [activeTab]);

  // Filter options
  const filterOptions: FilterOptions = useMemo(() => {
    if (activeTab === 'candidates') {
      return {
        months: getUniqueMonths(candidates),
        pocs: getUniquePocs(candidates),
      };
    } else {
      return {
        statuses: ['Settled', 'Pending'],
      };
    }
  }, [activeTab, candidates]);

  // Filter Configurations for Generic FilterBar
  const filterConfigs = useMemo(() => {
    if (activeTab === 'candidates') {
      return [
        {
          key: 'month',
          label: 'All Months',
          options: (filterOptions.months || []).map((m) => ({ label: m, value: m })),
        },
        {
          key: 'poc',
          label: 'All POCs',
          options: (filterOptions.pocs || []).map((p) => ({ label: p, value: p })),
        },
      ];
    } else {
      return [
        {
          key: 'status',
          label: 'All Statuses',
          options: (filterOptions.statuses || []).map((s) => ({ label: s, value: s })),
        },
      ];
    }
  }, [activeTab, filterOptions]);

  // Filtered and searched data
  const displayedData = useMemo(() => {
    // For POC, only show candidates (no invoices tab)
    const data = isPOC ? candidates : activeTab === 'candidates' ? candidates : invoices;
    let processed = searchData(data as any[], searchTerm);
    processed = filterData(processed, filters);
    return processed;
  }, [activeTab, candidates, invoices, searchTerm, filters, isPOC]);

  // Columns for Candidates table
  const candidateColumns = useMemo(() => [
    {
      Header: 'Candidate Name',
      accessor: 'candidate_name',
      sortable: true,
    },
    {
      Header: 'Job Title',
      accessor: 'job_title',
      sortable: true,
    },
    {
      Header: 'Hired Date',
      accessor: 'hired_date',
      sortable: true,
      Cell: ({ value }: { value: string }) => formatMonthYear(value),
    },
    {
      Header: 'POC Name',
      accessor: 'poc_name',
      sortable: true,
    },
    {
      Header: 'Fee Amount',
      accessor: 'fee_amount',
      sortable: true,
      Cell: ({ value }: { value: number }) => formatCurrency(value),
    },
  ], []);

  // Columns for Invoices table
  const isOrgAdmin = userRole === 'ORG_ADMIN';

  const invoiceColumns = useMemo(() => [
    {
      Header: 'Invoice Name',
      accessor: 'invoiceName',
      sortable: true,
    },
    {
      Header: 'Generated At',
      accessor: 'generatedDate',
      sortable: true,
      Cell: ({ value }: { value: string }) => formatDate(value),
    },
    {
      Header: 'Due At',
      accessor: 'dueDate',
      sortable: true,
      Cell: ({ value }: { value: string }) => formatDate(value),
    },
    {
      Header: 'Status',
      accessor: 'status',
      sortable: true,
      Cell: ({ value }: { value: string }) => (
        <span className={value === 'Settled' ? styles.statusSettled : styles.statusPending}>
          {value}
        </span>
      ),
    },
    ...(isOrgAdmin
      ? [
        {
          Header: 'Outlet Name',
          accessor: 'outletName',
          sortable: true,
        },
        {
          Header: 'Manager Name',
          accessor: 'managerName',
          sortable: true,
        },
      ]
      : []),
  ], [isOrgAdmin]);

  // Handle candidate row click
  const handleCandidateClick = (candidate: BillingCandidate) => {
    setSelectedCandidate(candidate);
  };

  // Handle invoice row click
  const handleInvoiceClick = (invoice: BillingInvoice) => {
    setSelectedInvoice(invoice);
  };

  // Handle search
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  // Handle filter change
  const handleFilterChange = (newFilters: Record<string, string>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Handle export
  const handleExport = () => {
    if (activeTab === 'candidates') {
      exportCandidatesToCSV(displayedData);
    } else {
      exportInvoicesToCSV(displayedData);
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (activeTab === 'candidates') {
      const data = displayedData as BillingCandidate[];
      const totalFees = data.reduce((sum, c) => sum + (c.fee_amount || 0), 0);

      return {
        total: data.length,
        settled: 0, // Not available anymore
        pending: 0, // Not available anymore
        totalAmount: totalFees,
      };
    } else {
      const data = displayedData as BillingInvoice[];
      const totalAmount = data.reduce((sum, inv) => sum + Number(inv.totalAmount || 0), 0);
      const settled = data.filter((inv) => inv.status === 'Settled').length;
      const pending = data.filter((inv) => inv.status === 'Pending').length;

      return {
        total: data.length,
        settled,
        pending,
        totalAmount,
        isLoading: invoicesLoading,
      };
    }
  }, [activeTab, displayedData, invoicesLoading]);

  const isLoading = candidatesLoading || invoicesLoading;

  if (isLoading) {
    return (
      <MainLayout user={user} onLogout={onLogout}>
        <div className={styles.adminPage}>
          <header className={styles.header}>
            <div>
              <h1 className={styles.title}>{isPOC ? 'History' : 'Billing & Invoicing'}</h1>
              <p className={styles.subtitle}>
                {isPOC
                  ? 'View your hiring history'
                  : 'Track hired candidates and manage monthly invoices'}
              </p>
            </div>
          </header>

          {/* Skeleton Stats Grid */}
          <div className={styles.statsGrid}>
            {[...Array(4)].map((_, i) => (
              <div key={i} className={styles.statCard} style={{ height: '100px' }}>
                <div className={styles.skeleton} style={{ width: '60%', height: '16px', marginBottom: '8px' }}></div>
                <div className={styles.skeleton} style={{ width: '40%', height: '32px' }}></div>
              </div>
            ))}
          </div>

          <div className={styles.tabs}>
            <div className={styles.tabSkeleton}></div>
            <div className={styles.tabSkeleton}></div>
          </div>

          <div className={styles.tableSkeleton}>
            {[...Array(5)].map((_, index) => (
              <div key={index} className={styles.skeletonRow}></div>
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className={styles.adminPage}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title} data-testid="billing-title">{isPOC ? 'History' : 'Billing & Invoicing'}</h1>
            <p className={styles.subtitle} data-testid="billing-subtitle">
              {isPOC
                ? 'View your hiring history'
                : 'Track hired candidates and manage monthly invoices'}
            </p>
          </div>
        </header>

        {/* Statistics Cards */}
        <div className={styles.statsGrid} data-testid="billing-stats-grid">
          <div className={styles.statCard} data-testid="stat-card-total">
            <h3>Total {isPOC || activeTab === 'candidates' ? 'Candidates' : 'Invoices'}</h3>
            <p className={styles.statValue}>{stats.total}</p>
          </div>
          <div className={styles.statCard} data-testid="stat-card-settled">
            <h3>Settled</h3>
            <p className={`${styles.statValue} ${styles.statValueSettled}`}>{stats.settled}</p>
          </div>
          <div className={styles.statCard} data-testid="stat-card-pending">
            <h3>Pending</h3>
            <p className={`${styles.statValue} ${styles.statValuePending}`}>{stats.pending}</p>
          </div>
          <div className={styles.statCard} data-testid="stat-card-amount">
            <h3>Total Amount</h3>
            <p className={styles.statValue}>{formatCurrency(stats.totalAmount)}</p>
          </div>
        </div>

        {/* Filter Bar */}
        <FilterBar
          key={activeTab} // Force remount when tab changes to reset internal state
          filters={filterConfigs}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          showExport={true}
          onExport={handleExport}
        />

        {/* Tabs - Hide invoices tab for POC */}
        {!isPOC && (
          <div data-testid="billing-tabs">
            <TabBar
              tabs={[
                { id: 'candidates', label: 'Hired Candidates' },
                { id: 'invoices', label: 'Invoices' },
              ]}
              activeTab={activeTab}
              onTabChange={(id) => setActiveTab(id as 'candidates' | 'invoices')}
            />
          </div>
        )}

        {/* Table */}
        {isPOC || activeTab === 'candidates' ? (
          <Table
            key="candidates-table"
            columns={candidateColumns}
            data={displayedData as BillingCandidate[]}
            onRowClick={handleCandidateClick}
            emptyMessage="No hired candidates found"
            pageSize={10}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        ) : (
          <Table
              key="invoices-table"
            columns={invoiceColumns}
            data={displayedData as BillingInvoice[]}
            onRowClick={handleInvoiceClick}
            emptyMessage="No invoices found"
            pageSize={10}
            pageSizeOptions={[5, 10, 25, 50]}
          />
        )}
      </div>

      {/* Candidate Details Panel (from ATS) */}
      {selectedCandidate && (
        <CandidateDetailsPanel
          candidate={{
            kind: 'candidate',
            id: selectedCandidate.application_id,
            candidate_id: selectedCandidate.candidate_id,
            name: selectedCandidate.candidate_name,
            column: 'ACCEPTED', // Assuming hired candidates are in ACCEPTED/HIRED state
            skillMatch: 0, // Not available in billing data
            matchingSkills: [],
            applied_at: selectedCandidate.hired_date,
            interviewId: null,
            interviewDetails: null,
            offerLetter: null, // Could potentially fetch this if needed
          } as unknown as Candidate}
          onClose={() => setSelectedCandidate(null)}
          showSkillMatch={false}
        // No action handlers for read-only view
        />
      )}

      {/* Invoice Modal - Only show for non-POC users */}
      {!isPOC && selectedInvoice && (
        <InvoiceModal
          invoice={selectedInvoice}
          canViewOutletInfo={isOrgAdmin}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </MainLayout>
  );
};

export default BillingPage;
